import type { AiWidget } from "@repo/shared/types";

/**
 * Parse widget XML tags from AI response text.
 * Returns the text with widget tags stripped, plus extracted widgets.
 */
export function parseWidgets(text: string): { cleanText: string; widgets: AiWidget[] } {
  const widgets: AiWidget[] = [];

  let cleanText = text;

  // <client-widget>id1,id2,id3</client-widget>
  cleanText = cleanText.replace(/<client-widget>([\s\S]*?)<\/client-widget>/g, (_match, ids) => {
    const clientIds = (ids as string)
      .split(",")
      .map((id: string) => id.trim())
      .filter(Boolean);
    if (clientIds.length > 0) {
      widgets.push({
        type: "client_list",
        clients: clientIds.map((id: string) => ({ id, name: id })),
      });
    }
    return "";
  });

  // <flow-widget>instance_id</flow-widget>
  cleanText = cleanText.replace(/<flow-widget>([\s\S]*?)<\/flow-widget>/g, (_match, id) => {
    const flowId = (id as string).trim();
    if (flowId) {
      widgets.push({
        type: "flow_instance",
        id: flowId,
        name: null,
        flowName: "",
        clientName: "",
        clientId: "",
        status: "",
        url: "",
      });
    }
    return "";
  });

  // <doc-widget id="page_id">Title</doc-widget> or <doc-widget>page_id</doc-widget>
  cleanText = cleanText.replace(
    /<doc-widget(?:\s+id="([^"]*)")?>([^<]*)<\/doc-widget>/g,
    (_match, idAttr, body) => {
      const pageId = ((idAttr as string) || (body as string)).trim();
      const title = idAttr ? (body as string).trim() : pageId;
      if (pageId) {
        widgets.push({
          type: "source_list",
          sources: [{ pageId, slug: pageId, title, snippet: "", relevanceScore: 0 }],
        });
      }
      return "";
    },
  );

  // <note-widget>note_id</note-widget>
  cleanText = cleanText.replace(/<note-widget>([\s\S]*?)<\/note-widget>/g, (_match, id) => {
    const noteId = (id as string).trim();
    if (noteId) {
      widgets.push({
        type: "note",
        id: noteId,
        title: "",
        preview: "",
        updatedAt: "",
        authorName: null,
      });
    }
    return "";
  });

  // <call-widget>call_id</call-widget>
  cleanText = cleanText.replace(/<call-widget>([\s\S]*?)<\/call-widget>/g, (_match, id) => {
    const callId = (id as string).trim();
    if (callId) {
      widgets.push({
        type: "call",
        id: callId,
        topic: "",
        preview: "",
        occurredAt: "",
        authorName: null,
      });
    }
    return "";
  });

  // <meeting-widget>meeting_id</meeting-widget>
  cleanText = cleanText.replace(/<meeting-widget>([\s\S]*?)<\/meeting-widget>/g, (_match, id) => {
    const meetingId = (id as string).trim();
    if (meetingId) {
      widgets.push({
        type: "meeting",
        id: meetingId,
        topic: "",
        preview: "",
        occurredAt: "",
        authorName: null,
      });
    }
    return "";
  });

  // <piechart-widget title="Title">Label1:Value1,Label2:Value2</piechart-widget>
  cleanText = cleanText.replace(
    /<piechart-widget\s+title="([^"]*)">([\s\S]*?)<\/piechart-widget>/g,
    (_match, title, body) => {
      const data = (body as string)
        .split(",")
        .map((entry: string) => {
          const [label, value] = entry.split(":").map((s: string) => s.trim());
          const num = Number(value);
          return label && value && !Number.isNaN(num) ? { label, value: num } : null;
        })
        .filter(Boolean) as Array<{ label: string; value: number }>;
      if (data.length > 0) {
        widgets.push({ type: "pie_chart", title: title as string, data });
      }
      return "";
    },
  );

  // <table-widget title="Optional title">
  //   col1 | col2 | col3
  //   r1c1 | r1c2 | r1c3
  //   r2c1 | r2c2 | r2c3
  // </table-widget>
  // First non-empty line is the header. Cells separated by unescaped `|`.
  // Use `\|` for a literal pipe inside a cell.
  cleanText = cleanText.replace(
    /<table-widget(?:\s+title="([^"]*)")?>([\s\S]*?)<\/table-widget>/g,
    (_match, title, body) => {
      const splitCells = (line: string): string[] =>
        line.split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, "|"));
      const lines = (body as string)
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      if (lines.length < 2) return "";
      const columns = splitCells(lines[0]!);
      if (columns.length === 0) return "";
      const rows = lines
        .slice(1)
        .map((line) => splitCells(line))
        .filter((cells) => cells.length === columns.length)
        .map((cells) =>
          cells.map((c) => {
            if (c === "" || c === "-" || c === "—") return null;
            const n = Number(c);
            return Number.isFinite(n) && c.match(/^-?\d+(\.\d+)?$/) ? n : c;
          }),
        );
      if (rows.length > 0) {
        widgets.push({
          type: "table",
          title: typeof title === "string" && title ? title : undefined,
          columns,
          rows,
        });
      }
      return "";
    },
  );

  // Clean up extra whitespace from removed tags
  cleanText = cleanText.replace(/\n{3,}/g, "\n\n").trim();

  return { cleanText, widgets };
}

/**
 * Merge widget lists dropping structural duplicates — the same widget can
 * arrive both via tool metadata and as an inline tag in the message text.
 */
export function dedupeWidgets(widgets: AiWidget[]): AiWidget[] {
  const seen = new Set<string>();
  return widgets.filter((w) => {
    const key = JSON.stringify(w);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
